import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Uploader } from "../components/Uploader";

const empty = {
  name: "", category_slug: "", address: "", description: "",
  services: [""], pricing: "", email: "", phone: "", website: "",
  social_links: { linkedin: "", twitter: "", instagram: "" },
  preferred_contact_time: "",
  logo: "",
  images: [], project_links: [""], documents: [""],
  // SEO
  slug: "", meta_title: "", meta_description: "", keywords: [""], og_image: "",
};

export default function ListBusiness({ editing = null, onSaved = null }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(editing || empty);
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);
  useEffect(() => { if (!user) nav("/login", { state: { from: "/list-business" } }); }, [user, nav]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setListItem = (k, i, v) => setForm((f) => ({ ...f, [k]: f[k].map((x, idx) => idx === i ? v : x) }));
  const addItem = (k) => setForm((f) => ({ ...f, [k]: [...f[k], ""] }));
  const rmItem = (k, i) => setForm((f) => ({ ...f, [k]: f[k].filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const payload = {
      ...form,
      services: form.services.filter(Boolean),
      images: (form.images || []).filter(Boolean),
      project_links: form.project_links.filter(Boolean),
      documents: form.documents.filter(Boolean),
      keywords: (form.keywords || []).filter(Boolean),
      logo: form.logo || null,
      og_image: form.og_image || null,
      meta_title: form.meta_title || null,
      meta_description: form.meta_description || null,
      slug: form.slug || null,
      social_links: Object.fromEntries(Object.entries(form.social_links || {}).filter(([_, v]) => v)),
    };
    try {
      if (editing) {
        const { data } = await api.patch(`/businesses/${editing.id}`, payload);
        toast.success("Listing updated");
        onSaved && onSaved(data);
      } else {
        const { data } = await api.post("/businesses", payload);
        toast.success("Listing published");
        nav(`/business/${data.id}`);
      }
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;
  const isFree = (user.membership === "free");
  const maxImages = isFree ? 1 : 12;

  return (
    <div className="mx-auto max-w-3xl px-6 lg:px-8 py-12" data-testid="list-business-page">
      <div className="mb-8">
        <div className="text-xs font-mono-tag uppercase tracking-[0.2em] text-zinc-400 mb-2">/ {editing ? "Edit" : "Create"}</div>
        <h1 className="font-display text-4xl tracking-tight">{editing ? "Edit listing" : "List your business"}</h1>
        <p className="mt-2 text-sm text-zinc-500">
          {isFree && "Free plan: 1 image, no project links / documents. Upgrade for unlimited."}
        </p>
      </div>

      <form onSubmit={submit} className="space-y-8">
        <Section title="Basics">
          <Row>
            <Field label="Business name *"><Input value={form.name} onChange={(e) => setField("name", e.target.value)} required data-testid="biz-name-input" /></Field>
            <Field label="Category *">
              <Select value={form.category_slug} onValueChange={(v) => setField("category_slug", v)}>
                <SelectTrigger data-testid="biz-category-trigger"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </Row>
          <Field label="Address"><Input value={form.address} onChange={(e) => setField("address", e.target.value)} data-testid="biz-address-input" /></Field>
          <Field label="Description"><Textarea rows={4} value={form.description} onChange={(e) => setField("description", e.target.value)} data-testid="biz-desc-input" /></Field>
        </Section>

        <Section title="Services & Pricing">
          <Field label="Services offered">
            {form.services.map((s, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <Input value={s} onChange={(e) => setListItem("services", i, e.target.value)} placeholder="e.g. Brand strategy" />
                <Button type="button" variant="outline" size="icon" onClick={() => rmItem("services", i)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => addItem("services")}><Plus className="h-4 w-4 mr-1" />Add service</Button>
          </Field>
          <Field label="Pricing"><Textarea rows={3} value={form.pricing} onChange={(e) => setField("pricing", e.target.value)} placeholder="e.g. From $5,000/mo retainer" /></Field>
        </Section>

        <Section title="Contact">
          <Row>
            <Field label="Email *"><Input type="email" required value={form.email} onChange={(e) => setField("email", e.target.value)} data-testid="biz-email-input" /></Field>
            <Field label="Phone"><Input value={form.phone} onChange={(e) => setField("phone", e.target.value)} /></Field>
          </Row>
          <Row>
            <Field label="Website"><Input value={form.website} onChange={(e) => setField("website", e.target.value)} placeholder="https://…" /></Field>
            <Field label="Preferred contact time"><Input value={form.preferred_contact_time} onChange={(e) => setField("preferred_contact_time", e.target.value)} placeholder="Weekdays 9-5 ET" /></Field>
          </Row>
          <Row>
            <Field label="LinkedIn"><Input value={form.social_links.linkedin || ""} onChange={(e) => setField("social_links", { ...form.social_links, linkedin: e.target.value })} data-testid="biz-social-linkedin" /></Field>
            <Field label="Twitter / X"><Input value={form.social_links.twitter || ""} onChange={(e) => setField("social_links", { ...form.social_links, twitter: e.target.value })} data-testid="biz-social-twitter" /></Field>
          </Row>
          <Field label="Instagram"><Input value={form.social_links.instagram || ""} onChange={(e) => setField("social_links", { ...form.social_links, instagram: e.target.value })} placeholder="https://instagram.com/yourbrand" data-testid="biz-social-instagram" /></Field>
        </Section>

        <Section title="Branding" hint="Your logo appears on your listing card and detail page.">
          <Field label="Logo">
            <Uploader value={form.logo || ""} onChange={(v) => setField("logo", v)} multi={false} label="Upload logo" />
            <div className="text-xs text-zinc-500 mt-2">…or paste a URL</div>
            <Input value={form.logo || ""} onChange={(e) => setField("logo", e.target.value)} placeholder="https://…/logo.png" data-testid="biz-logo-input" />
          </Field>
        </Section>

        <Section title="Media" hint={isFree ? "Upgrade to Pro/Premium for image galleries, project links, and documents." : ""}>
          <Field label={`Gallery images (max ${maxImages})`}>
            <Uploader value={form.images || []} onChange={(v) => setField("images", v)} multi={true} max={maxImages} label="Upload images" />
          </Field>
          {!isFree && (
            <>
              <Field label="Project links">
                {form.project_links.map((s, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <Input value={s} onChange={(e) => setListItem("project_links", i, e.target.value)} placeholder="https://…" />
                    <Button type="button" variant="outline" size="icon" onClick={() => rmItem("project_links", i)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => addItem("project_links")}><Plus className="h-4 w-4 mr-1" />Add link</Button>
              </Field>
              <Field label="Documents (URL)">
                {form.documents.map((s, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <Input value={s} onChange={(e) => setListItem("documents", i, e.target.value)} placeholder="https://…" />
                    <Button type="button" variant="outline" size="icon" onClick={() => rmItem("documents", i)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => addItem("documents")}><Plus className="h-4 w-4 mr-1" />Add document</Button>
              </Field>
            </>
          )}
        </Section>

        <Section title="SEO" hint="Optional. Boost discoverability on Google with a tailored snippet and keywords.">
          <Row>
            <Field label="URL slug">
              <Input value={form.slug || ""} onChange={(e) => setField("slug", e.target.value)} placeholder="auto-generated from name" data-testid="biz-seo-slug" />
            </Field>
            <Field label="OG image URL">
              <Input value={form.og_image || ""} onChange={(e) => setField("og_image", e.target.value)} placeholder="https://…/share.png" data-testid="biz-seo-og" />
            </Field>
          </Row>
          <Field label="Meta title">
            <Input value={form.meta_title || ""} onChange={(e) => setField("meta_title", e.target.value)} maxLength={70} placeholder="Up to 70 characters" data-testid="biz-seo-title" />
            <div className="text-[10px] text-zinc-400 font-mono-tag mt-1">{(form.meta_title || "").length}/70</div>
          </Field>
          <Field label="Meta description">
            <Textarea rows={3} value={form.meta_description || ""} onChange={(e) => setField("meta_description", e.target.value)} maxLength={160} placeholder="Up to 160 characters — appears under your title in search results" data-testid="biz-seo-desc" />
            <div className="text-[10px] text-zinc-400 font-mono-tag mt-1">{(form.meta_description || "").length}/160</div>
          </Field>
          <Field label="Keywords">
            {form.keywords.map((s, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <Input value={s} onChange={(e) => setListItem("keywords", i, e.target.value)} placeholder="e.g. brand strategy" />
                <Button type="button" variant="outline" size="icon" onClick={() => rmItem("keywords", i)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => addItem("keywords")} data-testid="biz-seo-add-kw"><Plus className="h-4 w-4 mr-1" />Add keyword</Button>
          </Field>
        </Section>

        {error && <div className="text-sm text-red-600" data-testid="biz-form-error">{error}</div>}

        <div className="flex items-center gap-3">
          <Button type="submit" className="btn-brand" disabled={busy} data-testid="biz-form-submit">
            {busy ? "Saving…" : editing ? "Save changes" : "Publish listing"}
          </Button>
          {!editing && <Button type="button" variant="outline" onClick={() => nav("/dashboard")}>Cancel</Button>}
        </div>
      </form>
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <section className="rounded-xl border border-zinc-200 p-6 bg-white">
      <div className="text-xs font-mono-tag uppercase tracking-[0.2em] text-zinc-400 mb-1">/ {title}</div>
      {hint && <div className="text-xs text-zinc-500 mb-4">{hint}</div>}
      <div className="space-y-4">{children}</div>
    </section>
  );
}
function Row({ children }) { return <div className="grid md:grid-cols-2 gap-4">{children}</div>; }
function Field({ label, children }) { return <div className="space-y-2"><Label>{label}</Label>{children}</div>; }
